import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Newspaper, Calendar, ArrowRight, Search, BookOpen, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Link } from 'react-router-dom';
import { resolveFileUrl } from '../../utils/fileUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ProgramsAndNewsPage = () => {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedNews, setSelectedNews] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [newsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/news`),
        axios.get(`${API}/events`)
      ]);
      setNews(newsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const filteredNews = news.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="gradient-orange py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-6">
            <a href="/" className="hover:text-white">Home</a>
            <span>›</span>
            <span>Programs and News</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Programs and News
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              कार्यक्रम एवं समाचार - Stay updated with our community programs and latest news
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-100 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 py-4">
            {['all', 'news', 'programs'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeTab === tab ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {tab === 'all' ? 'All' : tab === 'news' ? 'News' : 'Programs'}
              </button>
            ))}
            <div className="ml-auto relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10 border-gray-200"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* News Section */}
        {(activeTab === 'all' || activeTab === 'news') && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 section-heading">Latest News</h2>
            {filteredNews.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {filteredNews.slice(0, 6).map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-card hover:shadow-card-hover transition-all card-hover group cursor-pointer"
                    onClick={() => setSelectedNews(item)}
                    data-testid={`programs-news-card-${index}`}
                  >
                    {item.image && (
                      <div className="h-40 bg-gray-100 overflow-hidden">
                        <img src={resolveFileUrl(item.image)} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    )}
                    <div className="p-5">
                      <span className="text-sm text-primary font-semibold">{new Date(item.date).toLocaleDateString()}</span>
                      <h3 className="font-bold text-lg text-gray-900 mt-2 mb-2 line-clamp-2 group-hover:text-primary transition-colors">{item.title}</h3>
                      <p className="text-gray-600 text-sm line-clamp-2">{item.content}</p>
                      <span className="mt-3 text-primary font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read More <ArrowRight size={16} />
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No news available</p>
              </div>
            )}
          </section>
        )}

        {/* Programs/Events Section */}
        {(activeTab === 'all' || activeTab === 'programs') && (
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 section-heading">Programs & Events</h2>
            {events.length > 0 ? (
              <div className="space-y-4">
                {events.slice(0, 5).map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-xl p-6 border border-gray-100 shadow-card hover:shadow-card-hover transition-all flex gap-6"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-primary font-semibold mb-2">
                        <Calendar size={16} />
                        <span className="text-sm">{new Date(event.date).toLocaleDateString()}</span>
                      </div>
                      <h3 className="font-bold text-xl text-gray-900 mb-2">{event.title}</h3>
                      <p className="text-gray-600">{event.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No programs available</p>
              </div>
            )}
          </section>
        )}
      </div>

      {/* News Detail Modal */}
      <AnimatePresence>
        {selectedNews && (
          <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedNews(null)}
            data-testid="programs-news-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="programs-news-modal"
            >
              <div className="sticky top-0 bg-white z-10 flex justify-end p-4 border-b border-gray-100">
                <button
                  onClick={() => setSelectedNews(null)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  data-testid="programs-news-modal-close"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              {selectedNews.image && (
                <div className="w-full max-h-80 overflow-hidden">
                  <img
                    src={resolveFileUrl(selectedNews.image)}
                    alt={selectedNews.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  {selectedNews.category && (
                    <span className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                      {selectedNews.category}
                    </span>
                  )}
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <Calendar size={14} />
                    {new Date(selectedNews.date).toLocaleDateString()}
                  </span>
                </div>
                <h2 className="font-bold text-2xl md:text-3xl text-gray-900 mb-6">
                  {selectedNews.title}
                </h2>
                <div className="prose prose-gray max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {selectedNews.content}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProgramsAndNewsPage;
